/**
 * Lightweight draw control for the dashboard map editor.
 * Uses @mapbox/mapbox-gl-draw (already a dependency of the host app).
 */

import React from 'react';
import { useControl } from 'react-map-gl';
// @ts-ignore - Mapbox Draw types may not be fully compatible
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import type { ControlPosition } from 'react-map-gl';
import type { MapAnnotation } from '../types/webMap';

export interface MapDrawControlProps {
  position?: ControlPosition;
  onAnnotationCreate?: (annotation: MapAnnotation) => void;
  onAnnotationUpdate?: (annotation: MapAnnotation) => void;
  onAnnotationDelete?: (ids: string[]) => void;
  color?: string;
}

function featureToAnnotation(feature: any, color: string): MapAnnotation {
  return {
    id: feature.id || `ann_${Date.now()}`,
    geometry: feature.geometry,
    properties: feature.properties ?? {},
    color,
  };
}

const DRAW_STYLES = [
  {
    id: 'gl-draw-polygon-fill-inactive',
    type: 'fill',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: { 'fill-color': '#1976d2', 'fill-outline-color': '#1976d2', 'fill-opacity': 0.12 },
  },
  {
    id: 'gl-draw-polygon-fill-active',
    type: 'fill',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    paint: { 'fill-color': '#1976d2', 'fill-outline-color': '#1976d2', 'fill-opacity': 0.25 },
  },
  {
    id: 'gl-draw-polygon-stroke-inactive',
    type: 'line',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#1976d2', 'line-width': 2 },
  },
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#1976d2', 'line-width': 2 },
  },
  {
    id: 'gl-draw-line-inactive',
    type: 'line',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#1976d2', 'line-width': 2 },
  },
  {
    id: 'gl-draw-line-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#1976d2', 'line-width': 2.5 },
  },
  {
    id: 'gl-draw-point-inactive',
    type: 'circle',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Point'], ['==', 'meta', 'feature'], ['!=', 'mode', 'static']],
    paint: { 'circle-radius': 5, 'circle-color': '#1976d2' },
  },
  {
    id: 'gl-draw-point-active',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature'], ['==', 'active', 'true']],
    paint: { 'circle-radius': 7, 'circle-color': '#1976d2' },
  },
  {
    id: 'gl-draw-vertex-halo',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
    paint: { 'circle-radius': 7, 'circle-color': '#FFF' },
  },
  {
    id: 'gl-draw-vertex',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
    paint: { 'circle-radius': 5, 'circle-color': '#1976d2' },
  },
  {
    id: 'gl-draw-midpoint',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: { 'circle-radius': 4, 'circle-color': '#1976d2' },
  },
];

const MapDrawControl: React.FC<MapDrawControlProps> = ({
  position = 'top-right',
  onAnnotationCreate,
  onAnnotationUpdate,
  onAnnotationDelete,
  color = '#1976d2',
}) => {
  // @ts-ignore - MapboxDraw type
  useControl<any>(
    () =>
      new MapboxDraw({
        displayControlsDefault: false,
        controls: { polygon: true, line_string: true, point: true, trash: true },
        defaultMode: 'simple_select',
        styles: DRAW_STYLES,
      }),
    ({ map }) => {
      if (onAnnotationCreate) {
        map.on('draw.create', (e: any) => {
          onAnnotationCreate(featureToAnnotation(e.features[0], color));
        });
      }
      if (onAnnotationUpdate) {
        map.on('draw.update', (e: any) => {
          onAnnotationUpdate(featureToAnnotation(e.features[0], color));
        });
      }
      if (onAnnotationDelete) {
        map.on('draw.delete', (e: any) => {
          onAnnotationDelete(e.features.map((f: any) => f.id));
        });
      }
    },
    ({ map }) => {
      map.off('draw.create', () => {});
      map.off('draw.update', () => {});
      map.off('draw.delete', () => {});
    },
    { position: position as any },
  );

  return null;
};

export default MapDrawControl;
